import { DATA_API_SERVICE } from "@/appConfig";
import API from "@/api/api";
import { sortBy } from "lodash";
import CreditProgram, { CreditProgramAPIResponse, CreditProgramType } from "@/classes/creditProgram";
import StationCreditProgram, { StationCreditProgramAPIResponse } from "@/classes/stationCreditProgram";
import formatToAPIDate from "@/utils/formatToAPIDate";
import StationTransactionSession, { StationTransactionSessionAPIResponse } from "@/classes/stationTransactionSession";
import StationTransactionInterval, {
  StationTransactionIntervalAPIResponse
} from "@/classes/stationTransactionInterval";

async function findAll(includeArchived = false) {
  const response = await API.get<CreditProgramAPIResponse[]>(`${DATA_API_SERVICE}/creditprogram`);
  let creditPrograms = response.data.map(creditProgram => CreditProgram.fromAPI(creditProgram));
  if (!includeArchived) {
    creditPrograms = creditPrograms.filter(creditProgram => creditProgram.status !== "ARCHIVED");
  }
  return sortBy(creditPrograms, creditProgram => creditProgram.name);
}

async function findById(id: string) {
  const response = await API.get<CreditProgramAPIResponse>(`${DATA_API_SERVICE}/creditprogram/${id}`);
  return CreditProgram.fromAPI(response.data);
}

async function create(creditProgram: CreditProgram) {
  return await API.post(
    `${DATA_API_SERVICE}/creditprogram`,
    new CreditProgram({
      ...creditProgram,
      programType: CreditProgramType.temporary
    })
  );
}

async function createPrograms(creditPrograms: StationCreditProgram[]) {
  return Promise.all(
    creditPrograms.map(program => {
      return API.post(
        `${DATA_API_SERVICE}/creditprogram/station`,
        new StationCreditProgram({
          ...program
        })
      );
    })
  );
}

async function update(creditProgram: CreditProgram) {
  return await API.put(`${DATA_API_SERVICE}/creditprogram/${creditProgram.id}`, creditProgram);
}

async function remove(creditProgram: CreditProgram) {
  return await API.delete(`${DATA_API_SERVICE}/creditprogram/${creditProgram.id}`);
}

async function updateStationCreditProgram(creditProgram: StationCreditProgram) {
  return API.put(`${DATA_API_SERVICE}/creditprogram/station`, creditProgram);
}

async function removeStationCreditProgram(creditProgram: StationCreditProgram) {
  return API.delete(
    `${DATA_API_SERVICE}/creditprogram/${creditProgram.creditProgramId}/station/${creditProgram.stationId}`
  );
}

async function generateReport(start: Date, end: Date, creditProgramUUID: CreditProgram["id"]) {
  const response = await API.post<{
    data: {
      stationSerial: string;
      creditProgramName?: string;
      creditProgramId: string;
      totalKWh: number;
      jsonData?: object;
    }[];
  }>(`${DATA_API_SERVICE}/creditprogram/report`, {
    start: formatToAPIDate(start),
    end: formatToAPIDate(end),
    creditProgramUUID
  });
  return response.data.data;
}

async function getStationCreditPrograms(stationId: string, isAdmin: boolean) {
  if (!isAdmin) return [];
  try {
    const response = await API.get<StationCreditProgramAPIResponse[]>(
      `${DATA_API_SERVICE}/creditprogram/station/${stationId}`
    );
    return response.data.map(program => StationCreditProgram.fromAPI(program, stationId));
  } catch (e) {
    return [];
  }
}

async function getSCEReportSession(params: { start: Date; end: Date; creditProgramUUID: string }) {
  const response = await API.post<{
    data: {
      propertyId: string;
      propertyName: string;
      stationResults: StationTransactionSessionAPIResponse[];
    }[];
  }>(`${DATA_API_SERVICE}/creditprogram/report/sce/session`, params);
  return response.data.data.map(item => {
    return {
      ...item,
      stationResults: item.stationResults.map(result => StationTransactionSession.fromAPI(result))
    };
  });
}

async function getSCEReportInterval(params: {
  start: Date;
  end: Date;
  creditProgramUUID: string;
  propertyUUID?: string;
}) {
  const response = await API.post<{
    data: {
      propertyId: string;
      propertyName: string;
      stationResults: StationTransactionIntervalAPIResponse[];
    }[];
  }>(`${DATA_API_SERVICE}/creditprogram/report/sce/interval`, params);
  return response.data.data.map(item => {
    return {
      ...item,
      stationResults: item.stationResults.map(result => StationTransactionInterval.fromAPI(result))
    };
  });
}

const APICreditProgram = {
  getSCEReportSession,
  getSCEReportInterval,
  getStationCreditPrograms,
  createStationCreditPrograms: createPrograms,
  updateStationCreditProgram,
  removeStationCreditProgram,
  generateReport,
  findAll,
  findById,
  create: create,
  update,
  remove
};

export default APICreditProgram;
